import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  MenuItem,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  CheckCircleOutline,
  AccessTime,
  HighlightOff,
  PersonOff,
  Visibility,
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { useSnackbar } from "notistack";

import {
  createAssessmentsAPIEndpoint,
  EndPoints,
} from "../../../services/AssessmentAPI";

const AssessmentResults = () => {
  const { assessmentId } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [results, setResults] = useState([]);
  const [assessment, setAssessment] = useState(null);

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");

  const [page, setPage] = useState(1);
  const pageSize = 10;

  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchResults = async () => {
    setLoading(true);

    try {
      const res = await createAssessmentsAPIEndpoint(
        EndPoints.getAssessmentResults(assessmentId),
      ).fetch({ params: { search, status, page, pageSize } });

      setResults(res.data.items || []);
      setTotalCount(res.data.totalCount || 0);

      setAssessment({
        id: res.data.assessmentId,
        title: res.data.assessmentTitle,
        centerId: res.data.centerId,
        totalPoints: res.data.totalPoints,
      });

      setStatistics(
        res.data.statistics || {
          submitted: 0,
          inProgress: 0,
          timeOut: 0,
          notStarted: 0,
        },
      );
    } catch (error) {
      console.error(error);

      enqueueSnackbar("Failed to load assessment results.", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchResults();
  }, [assessmentId, search, status, page]);

  const [statistics, setStatistics] = useState({
    submitted: 0,
    inProgress: 0,
    timeOut: 0,
    notStarted: 0,
  });

  const getStatusInfo = (status) => {
    switch (status) {
      case "Submitted":
        return {
          label: "Submitted",
          className: "status-submitted",
          icon: <CheckCircleOutline fontSize="small" />,
        };

      case "InProgress":
        return {
          label: "In Progress",
          className: "status-progress",
          icon: <AccessTime fontSize="small" />,
        };

      case "TimeOut":
        return {
          label: "Missed",
          className: "status-timeout",
          icon: <HighlightOff fontSize="small" />,
        };

      case "NotStarted":
        return {
          label: "Not Started",
          className: "status-not-started",
          icon: <PersonOff fontSize="small" />,
        };

      default:
        return {
          label: status,
          className: "",
          icon: null,
        };
    }
  };

  const handlePageChange = (_, value) => {
    setPage(value);
  };

  const handleSearchChange = (e) => {
    setPage(1);
    setSearch(e.target.value);
  };

  const handleStatusChange = (e) => {
    setPage(1);
    setStatus(e.target.value);
  };

  const handleViewSubmission = (submission) => {
    if (!submission.submissionId) return;

    navigate(
      `/dashboard/assessments/${assessmentId}/results/${submission.submissionId}`,
    );
  };

  return (
    <section className="assessment-results-page">
      <Box className="assessment-results-container">
        {/* Header */}
        <div className="results-header">
          <div>
            <Typography className="results-title">
              {assessment?.title || "Assessment Results"}
            </Typography>

            <Typography className="results-subtitle">
              View students' performance and submission status
            </Typography>
          </div>

          <Button
            variant="outlined"
            className="back-button"
            onClick={() => navigate(-1)}
          >
            Back
          </Button>
        </div>

        {/* Statistics */}
        <div className="results-statistics">
          <Card className="stat-card stat-total">
            <CardContent>
              <div className="stat-icon">
                <PersonOff />
              </div>

              <div>
                <span>Total Students</span>
                <strong>{totalCount}</strong>
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card stat-success">
            <CardContent>
              <div className="stat-icon">
                <CheckCircleOutline />
              </div>

              <div>
                <span>Submitted</span>
                <strong>{statistics.submitted}</strong>
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card stat-progress">
            <CardContent>
              <div className="stat-icon">
                <AccessTime />
              </div>

              <div>
                <span>In Progress</span>
                <strong>{statistics.inProgress}</strong>
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card stat-warning">
            <CardContent>
              <div className="stat-icon">
                <HighlightOff />
              </div>

              <div>
                <span>Missed</span>
                <strong>{statistics.timeOut}</strong>
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card stat-neutral">
            <CardContent>
              <div className="stat-icon">
                <PersonOff />
              </div>

              <div>
                <span>Not Started</span>
                <strong>{statistics.notStarted}</strong>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="filters-card">
          <CardContent>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                fullWidth
                label="Search student"
                variant="outlined"
                value={search}
                onChange={handleSearchChange}
                placeholder="Search by student name..."
              />

              <TextField
                select
                label="Status"
                value={status}
                onChange={handleStatusChange}
                sx={{ minWidth: 200 }}
              >
                <MenuItem value="">All Students</MenuItem>
                <MenuItem value="Submitted">Submitted</MenuItem>
                <MenuItem value="InProgress">In Progress</MenuItem>
                <MenuItem value="TimeOut">Missed</MenuItem>
                <MenuItem value="NotStarted">Not Started</MenuItem>
              </TextField>
            </Stack>
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="results-table-card">
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Student</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Score</TableCell>
                  <TableCell>Percentage</TableCell>
                  <TableCell>Started At</TableCell>
                  <TableCell>Submitted At</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      align="center"
                      className="loading-cell"
                    >
                      <CircularProgress size={30} />
                    </TableCell>
                  </TableRow>
                ) : results.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      align="center"
                      className="empty-cell"
                    >
                      No students found.
                    </TableCell>
                  </TableRow>
                ) : (
                  results.map((student) => {
                    const statusInfo = getStatusInfo(student.status);

                    return (
                      <TableRow key={student.studentId} hover>
                        <TableCell>
                          <div className="student-info">
                            <div className="student-avatar">
                              {student.studentName?.charAt(0)?.toUpperCase()}
                            </div>

                            <div>
                              <strong>{student.studentName}</strong>

                              <span>{student.studentId}</span>
                            </div>
                          </div>
                        </TableCell>

                        <TableCell>
                          <div
                            className={`result-status ${statusInfo.className}`}
                          >
                            {statusInfo.icon}
                            {statusInfo.label}
                          </div>
                        </TableCell>

                        <TableCell>
                          {student.score != null
                            ? `${student.score} / ${assessment?.totalPoints}`
                            : "—"}
                        </TableCell>

                        <TableCell>
                          {student.percentage != null
                            ? `${student.percentage}%`
                            : "—"}
                        </TableCell>

                        <TableCell>
                          {student.startedAtUtc
                            ? new Date(student.startedAtUtc).toLocaleString()
                            : "—"}
                        </TableCell>

                        <TableCell>
                          {student.submittedAtUtc
                            ? new Date(student.submittedAtUtc).toLocaleString()
                            : "—"}
                        </TableCell>

                        <TableCell align="center">
                          {student.submissionId ? (
                            <Button
                              className="view-submission-btn"
                              startIcon={<Visibility />}
                              onClick={() => handleViewSubmission(student)}
                            >
                              View
                            </Button>
                          ) : (
                            <span className="no-action">—</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {totalCount > pageSize && (
            <div className="results-pagination">
              <Pagination
                count={Math.ceil(totalCount / pageSize)}
                page={page}
                onChange={handlePageChange}
                color="primary"
              />
            </div>
          )}
        </Card>
      </Box>
    </section>
  );
};

export default AssessmentResults;
